import type { PrescriptionImage } from '@/entities/performance/api/performance.api';
import {
  useClinicImages,
  useMonthlySummary,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { useRefreshableImageUrl } from '@/entities/performance/hooks/useRefreshableImageUrl';
import { monthOf } from '@/entities/performance/lib/performance';
import { useDeletePrescriptionImage } from '@/entities/prescription/hooks/usePrescriptionImages';
import { getApiErrorCode, isNetworkError } from '@/shared/api/http-client';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { formatDate } from '@/shared/utils/format';
import { showNetworkErrorToast, toastApi } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useState } from 'react';
import { ImageViewerDialog } from './ImageViewerDialog';
import { ImageStatusBadge } from './PerformanceParts';

/** 정산 확정 월 — [처방 등록]은 회색 비활성, ✕는 그리지 않는다 (M-07 §3) */
const SETTLED_REASON = '정산이 확정된 달은 등록 · 삭제할 수 없어요.';

/** 내 거래처가 아닌 병의원 — 처방 등록 API 경로(relationshipId)가 없다 */
const NO_RELATIONSHIP_REASON =
  '거래처로 등록된 병의원이 아니라 등록할 수 없어요.';

/** [처방 등록] — 비활성은 투명도 대신 회색 채움 (M-07 "회색 비활성") */
const REGISTER_BUTTON_CLASS =
  'h-10 w-[126px] rounded-[9px] text-[13px] font-bold disabled:bg-disabled disabled:text-ink-disabled disabled:opacity-100';

/** 썸네일 — presigned URL이 만료되면 다시 받는다. 원본이 없으면 회색 칸 */
const Thumbnail = ({
  ym,
  clinicId,
  image,
}: {
  ym: string;
  clinicId: number;
  image: PrescriptionImage;
}) => {
  const { src, onError } = useRefreshableImageUrl(ym, clinicId, image);
  return src ? (
    <img
      src={src}
      alt={`처방 이미지 ${image.displayName}`}
      loading="lazy"
      onError={onError}
      className="size-full object-cover"
    />
  ) : (
    <span className="text-[11px] leading-[17px] text-ink-7">
      이미지를 불러올 수 없어요
    </span>
  );
};

/**
 * M-16 처방 이미지 탭 — 썸네일 4열(모바일 2열), 썸네일을 누르면 M-18 뷰어
 * 원본 파일명은 저장되지 않아 서버 displayName(이미지 코드)을 라벨로 쓴다.
 *
 * 처방 수정(M-07 §3, 2026-10-15): 별도 수정 모달 없이 처방 확인 전(isConfirmed === false)
 * 썸네일의 ✕로 삭제한다. 뷰어(M-18)에는 삭제 진입점을 두지 않는다.
 */
export const ImagesTab = ({
  ym,
  clinicId,
  clinicName,
  relationshipId,
}: {
  ym: string;
  clinicId: number;
  clinicName: string;
  /** 본인 거래처 ID — 없으면 [처방 등록] 비활성 */
  relationshipId: number | null;
}) => {
  const images = useClinicImages(ym, clinicId);
  // ClinicSummary와 같은 쿼리(캐시 공유) — 정산 확정 여부
  const monthly = useMonthlySummary(ym, clinicId);
  const openUpload = useUIStore((s) => s.openPrescriptionUpload);
  const deleteImage = useDeletePrescriptionImage();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<PrescriptionImage | null>(null);

  const settled = !!monthly.data?.hasSettlement;
  // 확정 여부를 모르는 동안에는 ✕를 그리지 않는다 (서버도 확정 이미지 삭제는 막는다)
  const canDelete = !monthly.isPending && !settled;

  // M-06 처방 등록 모달 — 병의원을 이어받는다. 실적 화면에는 거래 상태가 없어 '거래 중'으로 연다.
  // 정산월은 서버가 정하므로 보던 달과 다르게 들어가면 완료 스낵바로 알린다
  const register = () =>
    relationshipId !== null &&
    openUpload({
      relationshipId,
      clinicName,
      statusView: '거래 중',
      viewingSettlementYearMonth: ym,
    });

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteImage.mutateAsync(deleting.imageCode);
      toastApi.success('1장이 삭제되었어요.');
    } catch (error) {
      if (getApiErrorCode(error) === 'PRESCRIPTION_ALREADY_EXISTS') {
        toastApi.error('이미 처방 확인이 끝난 이미지라 삭제할 수 없어요.');
        void images.refetch();
      } else if (isNetworkError(error)) {
        showNetworkErrorToast();
      } else {
        toastApi.error(
          '이미지를 삭제하지 못했어요. 잠시 후 다시 시도해주세요.',
        );
      }
    } finally {
      setDeleting(null);
    }
  };

  const list = images.data ?? [];

  const registerButton = (
    <Button
      size="md"
      className={REGISTER_BUTTON_CLASS}
      disabled={settled || relationshipId === null}
      onClick={register}
    >
      처방 등록
    </Button>
  );
  const blockedReason = settled
    ? SETTLED_REASON
    : relationshipId === null
      ? NO_RELATIONSHIP_REASON
      : null;
  const settledReason = blockedReason && (
    <p className="text-xs leading-[18px] text-ink-6">{blockedReason}</p>
  );

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 lg:min-h-[42px] lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <p className="text-[13px] leading-5 font-bold text-ink">
            {ym.slice(0, 4)}년 {monthOf(ym)}월 처방 이미지 ({list.length})
          </p>
          <p className="text-xs leading-[18px] text-[#a8a8a8]">
            등록한 처방 내역 원본입니다
          </p>
        </div>
        {/* 데스크톱: 사유 문구를 버튼 왼쪽에 둔다 */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 lg:flex-row-reverse">
          {registerButton}
          {settledReason}
        </div>
      </div>

      <div className="mt-4">
        {images.isPending ? (
          <LoadingBlock className="h-[208px]" />
        ) : images.isError ? (
          <ErrorState onRetry={() => void images.refetch()} />
        ) : list.length === 0 ? (
          <EmptyState
            className="rounded-xl border border-dashed border-line py-14"
            message="이 달에 등록한 처방 이미지가 없어요."
            action={
              <div className="flex flex-col items-center gap-2">
                {registerButton}
                {settledReason}
              </div>
            }
          />
        ) : (
          <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-5">
            {list.map((img, i) => (
              <li key={img.imageCode} className="relative">
                <button
                  type="button"
                  onClick={() => setViewerIndex(i)}
                  className="flex w-full flex-col overflow-hidden rounded-lg border border-line bg-surface text-left transition-colors hover:border-line-strong"
                >
                  <span className="flex h-[130px] w-full items-center justify-center overflow-hidden bg-muted">
                    <Thumbnail ym={ym} clinicId={clinicId} image={img} />
                  </span>
                  <span className="flex flex-col gap-1.5 px-3.5 py-3">
                    <span className="truncate text-xs leading-[18px] font-medium text-ink">
                      {img.displayName}
                    </span>
                    <span className="flex flex-wrap items-center justify-between gap-1">
                      <span className="text-[11px] leading-[17px] text-[#8d8d8d]">
                        {formatDate(img.createdAt)}
                      </span>
                      <ImageStatusBadge image={img} />
                    </span>
                  </span>
                </button>
                {/* 처방 확인 전 이미지에만 ✕ — 확인 완료 · 정산 확정 월은 그리지 않는다.
                    뷰어 버튼과 형제로 두어 ✕ 클릭이 뷰어를 열지 않게 한다 */}
                {canDelete && img.isConfirmed === false && (
                  <button
                    type="button"
                    aria-label={`${img.displayName} 삭제`}
                    onClick={() => setDeleting(img)}
                    className="absolute top-[11px] right-3 flex h-[26px] w-7 items-center justify-center rounded-[14px] bg-ink/72 text-[11px] leading-4 font-medium text-white transition-colors outline-none hover:bg-ink focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {list.length > 0 && (
        <p className="mt-3.5 text-[11.5px] leading-[17px] text-[#a8a8a8]">
          썸네일을 클릭하면 원본 뷰어가 열립니다. 처방 확인 전(확인 중) 이미지만
          ✕ 로 삭제할 수 있고, 확인 완료된 이미지는 삭제할 수 없습니다. 추가
          등록은 [처방 등록]으로 합니다.
        </p>
      )}

      {viewerIndex !== null && list.length > 0 && (
        <ImageViewerDialog
          images={list}
          index={Math.min(viewerIndex, list.length - 1)}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          ym={ym}
          clinicId={clinicId}
          clinicName={clinicName}
        />
      )}

      {/* M-07 §3 삭제 확인 팝업 — [취소]가 위(검정), [삭제]가 아래(회색 바탕 · 빨강 글자) */}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => !o && !deleteImage.isPending && setDeleting(null)}
      >
        <AlertDialogContent className="items-center text-center">
          <AlertDialogTitle>이미지를 삭제할까요?</AlertDialogTitle>
          <AlertDialogDescription className="text-xs leading-[19px] break-all">
            {deleting?.displayName} 1장을 삭제합니다.
            <br />
            삭제하면 되돌릴 수 없고, 그 달 등록 장수에서 빠집니다.
          </AlertDialogDescription>
          <AlertDialogFooter className="mt-3 w-full flex-col [&>*]:flex-none">
            <AlertDialogCancel
              disabled={deleteImage.isPending}
              className="bg-ink text-white hover:bg-ink/85"
            >
              취소
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteImage.isPending}
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              className="border border-[#f7d4d4] bg-muted text-danger hover:bg-danger-soft"
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
